import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import SegmentedSwitch from "../components/SegmentedSwitch";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { useAudience } from "../context/AudienceContext";
import { findRoomSet } from "../data/shop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Kontakt() {
  useDocumentTitle(
    "Projekt anfragen & Kontakt",
    "Erzählen Sie uns von Ihrem Ferienobjekt. Livinora erstellt ein belastbares erstes Konzeptangebot für Privat- und Gewerbekunden."
  );
  const { audience } = useAudience();
  const [params] = useSearchParams();

  // Shop / PDF pre-fill context (from /shop/:slug → ?source=shop|pdf_request&set=…&style=…&qty=…)
  const shopContext = useMemo(() => {
    const source = params.get("source");
    if (source !== "shop" && source !== "pdf_request") return null;
    const setSlug = params.get("set") || "";
    const set = findRoomSet(setSlug);
    if (!set) return null;
    const qtyRaw = parseInt(params.get("qty") || "1", 10);
    const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
    return { set, qty, source };
  }, [params]);

  const buildShopContextMessage = (ctx) => {
    if (!ctx) return "";
    const prefix = ctx.source === "pdf_request" ? "Anfrage Datenblatt:" : "Anfrage zu Set:";
    return `${prefix} ${ctx.set.name} (Stilserie: ${ctx.set.style_label}). Anzahl Einheiten: ${ctx.qty}.`;
  };

  const initialMessage = buildShopContextMessage(shopContext);

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    object_type: "",
    location: "",
    units: shopContext ? String(shopContext.qty) : "",
    project_kind: "neu",
    message: initialMessage,
    brochure_requested: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Re-sync if context changes (e.g., user navigates between shop sets without remount)
  useEffect(() => {
    if (!shopContext) return;
    const newMsg = buildShopContextMessage(shopContext);
    setForm((f) => ({
      ...f,
      units: String(shopContext.qty),
      message:
        f.message && !f.message.startsWith("Anfrage zu Set:") && !f.message.startsWith("Anfrage Datenblatt:")
          ? f.message
          : newMsg,
    }));
  }, [shopContext]);

  const update = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Bitte Name und E-Mail angeben.");
      return;
    }
    try {
      setSending(true);
      const payload = {
        audience,
        name: form.name,
        company: form.company || undefined,
        email: form.email,
        phone: form.phone || undefined,
        object_type: form.object_type || undefined,
        location: form.location || undefined,
        units: form.units ? Number(form.units) : undefined,
        project_kind: form.project_kind,
        message: form.message || undefined,
        brochure_requested: form.brochure_requested,
        source: shopContext ? shopContext.source : "kontakt",
        room_set: shopContext ? shopContext.set.slug : undefined,
        style_series: shopContext ? shopContext.set.style : undefined,
      };
      await axios.post(`${API}/inquiries`, payload);
      setSent(true);
      toast.success("Anfrage gesendet. Wir melden uns zeitnah.");
    } catch (err) {
      toast.error("Anfrage konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-kontakt" className="pt-28 sm:pt-32 md:pt-44 pb-20 sm:pb-24 md:pb-32">
      <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-12 sm:gap-14 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="overline">Projekt anfragen</span>
          <h1 className="serif mt-6 sm:mt-8 text-[40px] leading-[1.04] sm:text-5xl md:text-6xl lg:text-7xl sm:leading-[1.0] text-balance">
            Erzählen Sie uns von Ihrem Objekt.
          </h1>
          <p className="mt-6 sm:mt-8 text-[color:var(--livinora-muted)] leading-relaxed max-w-md">
            Eine Anfrage. Ein klarer Gesprächsrahmen. Ein belastbares erstes Konzeptangebot.
          </p>

          <div className="mt-10 sm:mt-12">
            <div className="overline">Nutzerkreis</div>
            <div className="mt-4">
              <SegmentedSwitch testId="kontakt-audience" />
            </div>
          </div>

          <div className="mt-10 sm:mt-14">
            <div className="overline">Kontakt</div>
            <p className="mt-4 text-sm text-[color:var(--livinora-muted)] leading-relaxed max-w-md">
              Schreiben Sie uns über das Formular — wir melden uns persönlich zurück.
              Postanschrift und alle weiteren Kontaktdaten finden Sie im{" "}
              <Link to="/impressum" className="underline-offset-4 hover:underline text-[color:var(--livinora-ink)]">
                Impressum
              </Link>.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {/* Shop context block */}
          {shopContext && !sent && (
            <Reveal>
              <div
                className="hairline border bg-[color:var(--livinora-paper-warm)] p-6 sm:p-8 mb-6 flex flex-col sm:flex-row gap-5 sm:gap-6 items-start"
                data-testid="kontakt-shop-context"
              >
                <div className="media-wrap aspect-[4/5] w-24 sm:w-28 shrink-0">
                  <img
                    src={shopContext.set.image}
                    alt={shopContext.set.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <span className="overline" data-testid="kontakt-shop-source">
                    {shopContext.source === "pdf_request" ? "Datenblatt-Anforderung" : "Ausgewähltes Set"}
                  </span>
                  <h2
                    className="serif mt-2 text-2xl sm:text-3xl leading-[1.05]"
                    data-testid="kontakt-shop-set-name"
                  >
                    {shopContext.set.name}
                  </h2>
                  <p className="mt-2 text-sm text-[color:var(--livinora-muted)] leading-relaxed">
                    {shopContext.source === "pdf_request"
                      ? "Sie fordern aktuell das Datenblatt für dieses Set an. Wir senden es nach Eingang Ihrer Daten zu."
                      : "Sie fragen aktuell dieses Standard-Set an. Wir bestätigen Verfügbarkeit, Richtpreis und Lieferung im persönlichen Angebot."}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.18em] text-[color:var(--livinora-muted)]">
                    <span>Stilserie · {shopContext.set.style_label}</span>
                    <span>·</span>
                    <span data-testid="kontakt-shop-qty">
                      {shopContext.qty} {shopContext.qty === 1 ? "Einheit" : "Einheiten"}
                    </span>
                  </div>
                  <div className="mt-4">
                    <Link
                      to={`/shop/${shopContext.set.slug}`}
                      className="text-xs uppercase tracking-[0.18em] underline-offset-4 hover:underline text-[color:var(--livinora-ink)]"
                      data-testid="kontakt-shop-change"
                    >
                      Set ändern
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {!sent ? (
            <Reveal>
              <form onSubmit={submit} className="hairline border bg-[color:var(--livinora-paper)] p-6 sm:p-8 md:p-12" data-testid="inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  <Field label="Name *">
                    <input className="field" required value={form.name} onChange={update("name")} data-testid="field-name" />
                  </Field>
                  <Field label="Unternehmen">
                    <input className="field" value={form.company} onChange={update("company")} data-testid="field-company" />
                  </Field>
                  <Field label="E-Mail *">
                    <input type="email" required className="field" value={form.email} onChange={update("email")} data-testid="field-email" />
                  </Field>
                  <Field label="Telefon">
                    <input className="field" value={form.phone} onChange={update("phone")} data-testid="field-phone" />
                  </Field>
                  <Field label="Objekttyp">
                    <select className="field" value={form.object_type} onChange={update("object_type")} data-testid="field-object">
                      <option value="">Bitte wählen …</option>
                      <option value="ferienhaus">Ferienhaus</option>
                      <option value="ferienwohnung">Ferienwohnung</option>
                      <option value="apartment">Apartment</option>
                      <option value="refit">Refit / Bestand</option>
                      <option value="gewerblich">Mehrere Einheiten</option>
                    </select>
                  </Field>
                  <Field label="Standort / Region">
                    <input className="field" value={form.location} onChange={update("location")} data-testid="field-location" />
                  </Field>
                  <Field label="Anzahl Einheiten">
                    <input type="number" min="0" className="field" value={form.units} onChange={update("units")} data-testid="field-units" />
                  </Field>
                  <Field label="Projektart">
                    <select className="field" value={form.project_kind} onChange={update("project_kind")} data-testid="field-kind">
                      <option value="neu">Neues Projekt</option>
                      <option value="refit">Refit / Modernisierung</option>
                    </select>
                  </Field>
                </div>

                <div className="mt-8">
                  <Field label="Ihre Nachricht">
                    <textarea className="field resize-none" rows={5} value={form.message} onChange={update("message")} data-testid="field-message" />
                  </Field>
                </div>

                <label className="mt-8 flex items-start gap-3 text-sm text-[color:var(--livinora-muted)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.brochure_requested}
                    onChange={update("brochure_requested")}
                    className="w-5 h-5 mt-[2px] accent-[color:var(--livinora-ink)] shrink-0"
                    data-testid="field-brochure"
                  />
                  <span>Broschüre zusätzlich per E-Mail zusenden.</span>
                </label>

                <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 sm:justify-between">
                  <p className="text-xs text-[color:var(--livinora-muted)] max-w-sm">
                    Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.
                  </p>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn-livinora btn-primary disabled:opacity-50 w-full sm:w-auto justify-center sm:justify-start"
                    data-testid="inquiry-submit"
                  >
                    {sending ? "Senden …" : "Anfrage senden"} <span>→</span>
                  </button>
                </div>
              </form>
            </Reveal>
          ) : (
            <Reveal>
              <div className="hairline border bg-[color:var(--livinora-paper-warm)] p-10 md:p-14" data-testid="inquiry-success">
                <span className="overline">Erhalten</span>
                <h2 className="serif text-4xl md:text-5xl mt-4 leading-[1.02]">
                  Vielen Dank — wir melden uns in Kürze.
                </h2>
                <p className="mt-6 text-[color:var(--livinora-muted)] max-w-lg leading-relaxed">
                  Ihre Anfrage ist bei uns eingegangen. Sie erhalten zeitnah eine persönliche
                  Rückmeldung mit nächsten Schritten.
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}
